import { TURNSTILE_SITE_KEY } from '@/constants/captcha'

export interface TurnstileRenderOptions {
  sitekey: string
  action?: string
  size?: 'normal' | 'compact' | 'flexible'
  'refresh-expired'?: 'auto' | 'manual' | 'never'
  callback?: (token: string) => void
  'error-callback'?: (code?: string | number) => void
  'expired-callback'?: () => void
  'before-interactive-callback'?: () => void
  'after-interactive-callback'?: () => void
}

interface TurnstileApi {
  render: (
    container: string | HTMLElement,
    options: TurnstileRenderOptions
  ) => string
  reset: (widgetId?: string) => void
  remove: (widgetId: string) => void
}

declare global {
  interface Window {
    turnstile?: TurnstileApi
  }
}

const TURNSTILE_SCRIPT_URL =
  'https://challenges.cloudflare.com/turnstile/v0/api.js'

function debug(...args: unknown[]) {
  if (import.meta.env.DEV) {
    console.info('[turnstile]', ...args)
  }
}

let scriptPromise: Promise<TurnstileApi> | null = null

function loadTurnstileScript(): Promise<TurnstileApi> {
  if (window.turnstile) {
    return Promise.resolve(window.turnstile)
  }

  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script')
      script.src = TURNSTILE_SCRIPT_URL
      script.async = true
      script.defer = true
      script.onload = () => {
        if (window.turnstile) {
          resolve(window.turnstile)
        } else {
          // Clear the cached promise so a later render() can retry the
          // load instead of inheriting this failure forever.
          scriptPromise = null
          reject(
            new Error('Turnstile script loaded but window.turnstile is missing')
          )
        }
      }
      script.onerror = () => {
        scriptPromise = null
        reject(new Error('Failed to load Turnstile script'))
      }
      document.head.appendChild(script)
    })
  }

  return scriptPromise
}

/**
 * Standard always-visible Managed Turnstile widget.
 *
 * The widget mounts under the form and starts solving immediately; whether
 * the user must actually interact is Cloudflare's decision, not ours. The
 * solved token lands in `storedToken` via the widget callback and is
 * reported through `onTokenAvailability`, so the caller can gate its
 * submit control on it. `getToken()` hands the token out synchronously and
 * resets the widget to solve the next one — tokens are single-use at
 * siteverify, so a consumed token must never be handed out twice.
 *
 * Expired tokens re-solve silently ('refresh-expired: auto'), announcing
 * unavailability in between so a stale token is never handed out.
 */
export function useTurnstile() {
  let api: TurnstileApi | null = null
  let widgetId: string | undefined
  let storedToken = ''
  let onTokenAvailability: ((hasToken: boolean) => void) | null = null
  let onError: (() => void) | null = null
  let onInteractivity: ((interactive: boolean) => void) | null = null
  // render() awaits the script load, so a remove() or a newer render() can
  // overtake an in-flight call. Each call takes a fresh generation; a call
  // that resumes to find itself outdated bails out instead of mounting a
  // widget the caller no longer wants.
  let renderGeneration = 0

  // Single write path for storedToken so the caller's availability
  // callback can never drift from the actual state — it drives UI gating
  // (the login submit stays disabled while the widget has no token).
  function setStoredToken(token: string) {
    storedToken = token
    onTokenAvailability?.(!!token)
  }

  /**
   * Resolves to false when the widget could not be mounted (script blocked
   * by an ad/content blocker, network failure, no sitekey configured) —
   * the caller must surface that to the user, who is otherwise stuck at a
   * token-locked submit with no working challenge and no explanation.
   *
   * `onTokenAvailability`: called with true whenever a solved token is
   * waiting to be handed out and with false whenever it no longer is
   * (handed out, expired, widget removed).
   *
   * `onError`: called when the mounted widget reports a challenge error —
   * the script loaded but the challenge itself failed (challenge requests
   * blocked by an ad/content blocker, network failure). render() resolving
   * true only covers mount failures, so without this signal a blocked
   * challenge would leave the caller waiting for a token that never comes.
   * Turnstile auto-retries transient errors, so a later token can still
   * arrive after an onError (reported via `onTokenAvailability`).
   *
   * `onInteractivity`: called with true when the widget switches into
   * interactive mode (Cloudflare demands a user click) and false when
   * the interaction completed. While interactive, the widget is waiting
   * on the *user*, not the network — no-token time in that window means
   * nothing, and the widget's own UI explains the wait.
   */
  async function render(
    container: HTMLElement,
    options?: {
      onTokenAvailability?: (hasToken: boolean) => void
      onError?: () => void
      onInteractivity?: (interactive: boolean) => void
    }
  ): Promise<boolean> {
    // Unconfigured environment: skip the third-party script load entirely.
    if (!TURNSTILE_SITE_KEY) {
      debug('render: no sitekey configured')
      return false
    }

    const generation = ++renderGeneration

    debug('render: start')
    try {
      api = await loadTurnstileScript()
    } catch (err) {
      debug('render: script load failed', err)
      return false
    }

    if (generation !== renderGeneration) {
      debug('render: superseded while loading, bailing out')
      return false
    }

    onTokenAvailability = options?.onTokenAvailability ?? null
    onError = options?.onError ?? null
    onInteractivity = options?.onInteractivity ?? null
    setStoredToken('')

    try {
      // Replace, never stack: callers normally remove() before re-rendering,
      // but that only clears a widget that had already mounted.
      if (widgetId) {
        api.remove(widgetId)
        widgetId = undefined
      }
      widgetId = api.render(container, {
        sitekey: TURNSTILE_SITE_KEY,
        // Explicit-render equivalent of data-action. The backend rejects
        // tokens whose siteverify `action` claim differs from its
        // CAPTCHA_EXPECTED_ACTION setting — keep the two in sync.
        action: 'turnstile-spin-v2',
        size: 'normal',
        'refresh-expired': 'auto',
        callback: (token) => {
          debug('callback: token', token ? `len=${token.length}` : '(empty)')
          setStoredToken(token)
        },
        'error-callback': (code) => {
          debug('error-callback: code', code)
          setStoredToken('')
          onError?.()
        },
        'expired-callback': () => {
          // 'refresh-expired: auto' re-runs the challenge by itself; the
          // stale token just must not be handed out meanwhile.
          debug('expired-callback')
          setStoredToken('')
        },
        'before-interactive-callback': () => {
          debug('before-interactive-callback')
          onInteractivity?.(true)
        },
        'after-interactive-callback': () => {
          debug('after-interactive-callback')
          onInteractivity?.(false)
        },
      })
    } catch (err) {
      debug('render: api.render threw', err)
      widgetId = undefined
      return false
    }

    debug('render: widget mounted', widgetId)
    return true
  }

  /**
   * Hand out the solved token, or '' when none is available (widget not
   * mounted / load failed / still solving — a caller that gates its
   * submit on onTokenAvailability never observes any of these).
   */
  function getToken(): string {
    if (!storedToken) {
      debug('getToken: no token available')
      return ''
    }
    const token = storedToken
    setStoredToken('')
    // The token is consumed by siteverify: reset so the widget starts
    // solving the next one right away — without it a failed login would
    // strand a spent widget behind the token-gated submit button.
    if (widgetId && api) {
      try {
        api.reset(widgetId)
      } catch (err) {
        // Cleanup-adjacent call inside logIn(): a throw here would skip
        // the submit and wedge the form, so log and move on.
        debug('getToken: api.reset threw', err)
      }
    }
    debug('getToken: handing out token')
    return token
  }

  function remove(): void {
    debug('remove')
    // Invalidate any render() still awaiting the script load — it must
    // not mount a widget the caller has already asked to be gone.
    renderGeneration += 1
    if (widgetId && api) {
      try {
        api.remove(widgetId)
      } catch (err) {
        // The widget's DOM may already be gone (v-if tore down the
        // container). Cleanup must never throw: a throw here would
        // propagate into component teardown.
        debug('remove: api.remove threw', err)
      }
    }
    widgetId = undefined
    setStoredToken('')
    onTokenAvailability = null
    onError = null
    onInteractivity = null
  }

  return { render, getToken, remove }
}
